import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Chip,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const ExpenseCategoryManagement = () => {
  const [categories, setCategories] = useState([]);

  // ONLY expense accounts, and the server refuses anything else.
  //
  // This was a free-text "Account Code" box. Nothing said which account it wanted, and the
  // natural reading of an unlabelled account field is the one the money comes out of — so
  // BANK CHARGES was configured against '1 CASH' and every expense in it posted as a
  // bank-to-cash transfer that reached no expense account and no profit and loss. The
  // charge type screen has always used a filtered picker for the same decision and has
  // never had the problem.
  const [accounts, setAccounts] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, reset,
    rows: visibleCategories, resultCount, totalCount
  } = useTableSearch(categories, {
    searchFields: ['name', 'accountCode', 'description'],
    filters: {}
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  // No default account. '5000' used to be prefilled, so a category nobody had thought
  // about still saved and still posted somewhere plausible. Choosing the account is the
  // decision that makes a category mean anything, so the form asks for it.
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ledgerAccountId: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get('/accounting/chart-of-accounts');
      setAccounts((res.data || []).filter((a) => a.type === 'expense' && a.isActive));
    } catch (error) {
      toast.error('Could not load the chart of accounts');
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/expense-categories');
      setCategories(res.data || []);
    } catch (error) {
      toast.error('Error fetching expense categories');
    }
  };

  const handleOpen = (category = null) => {
    if (category) {
      setEditing(category._id);
      setFormData({
        name: category.name,
        description: category.description || '',
        // Falls back to matching on code for categories configured before the id existed,
        // so editing one preselects its account rather than looking unset.
        ledgerAccountId: category.ledgerAccountId
          || (accounts.find((a) => a.code === category.accountCode)?._id ?? '')
      });
    } else {
      setEditing(null);
      setFormData({
        name: '',
        description: '',
        ledgerAccountId: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({
      name: '',
      description: '',
      ledgerAccountId: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    if (!formData.ledgerAccountId) {
      toast.error('Choose the expense account this category is charged to');
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        const res = await axios.put(`/expense-categories/${editing}`, formData);
        // A rename is carried across to existing expenses; say so, since it silently
        // changes rows the admin isn't looking at.
        const renamed = res.data?.renamedExpenses || 0;
        toast.success(
          renamed > 0
            ? `Category updated — ${renamed} existing expense(s) renamed to match`
            : 'Expense category updated successfully'
        );
      } else {
        await axios.post('/expense-categories', formData);
        toast.success('Expense category created successfully');
      }
      fetchCategories();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving expense category');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (category) => {
    try {
      await axios.put(`/expense-categories/${category._id}`, {
        isActive: !category.isActive
      });
      toast.success(`Expense category ${!category.isActive ? 'activated' : 'deactivated'} successfully`);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating expense category');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>
          Expense Category Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Category
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Manage expense categories that can be used when recording expenses. Each category can be linked to a specific account code from the Chart of Accounts.
      </Alert>

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Name or account code"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Charged to</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No expense categories found. Click "Add Category" to create one.
                </TableCell>
              </TableRow>
            ) : (
              visibleCategories.map((category) => (
                <TableRow key={category._id}>
                  <TableCell sx={{ fontWeight: 500 }}>{category.name}</TableCell>
                  <TableCell>{category.description || '-'}</TableCell>
                  <TableCell>
                    {/* The name, not just the code. '1' looked like a reasonable value for
                        as long as the screen never said it meant CASH. A category still
                        pointing at a non-expense account is called out here rather than
                        waiting to fail at the next approval. */}
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {category.accountCode}
                    </Typography>
                    {category.ledgerAccount ? (
                      <Typography
                        variant="caption"
                        color={category.ledgerAccount.type === 'expense' ? 'text.secondary' : 'error'}
                      >
                        {category.ledgerAccount.name}
                        {category.ledgerAccount.type !== 'expense'
                          && ` — ${category.ledgerAccount.type}, not an expense account`}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="error">
                        no such account — expenses in this category cannot be approved
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={category.isActive ? 'Active' : 'Inactive'}
                      color={category.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpen(category)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(category)}
                      color={category.isActive ? 'warning' : 'success'}
                    >
                      {category.isActive ? <DeleteIcon /> : <AddIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Edit Expense Category' : 'Add Expense Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              helperText="Unique name for this expense category"
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Optional description for this category"
            />
            <TextField
              select
              fullWidth
              label="Charged to"
              value={formData.ledgerAccountId}
              onChange={(e) => setFormData({ ...formData, ledgerAccountId: e.target.value })}
              margin="normal"
              required
              helperText={
                accounts.length
                  ? 'The expense account this category is charged to — not the bank or cash '
                    + 'account it is paid from, which is chosen on each expense.'
                  : 'No active expense accounts in the chart of accounts.'
              }
            >
              {accounts.map((account) => (
                <MenuItem key={account._id} value={account._id}>
                  {account.code} — {account.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.name.trim()}
          >
            {loading ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ExpenseCategoryManagement;

